import { EventCard, YesObject } from '@yes.technology/react-toolkit'

import { useFetchYesObjects } from 'yesObject/shared'
import useYesObject from 'yesObject/shared/hooks/useYesObject'

type EventProps = {
  object: YesObject
  variant?: React.ComponentProps<typeof EventCard>['variant']
}

type DocumentObjectProps = YesObject & {
  url?: YesObject[]
}

type CardVariant = Exclude<
  React.ComponentProps<typeof EventCard>['variant'],
  undefined
>

type DocumentFormat = 'landscape' | 'portrait' | 'square'

type AllVariants = CardVariant | DocumentFormat

const documentFormatToVariantMap: Record<AllVariants, CardVariant> = {
  // Map document formats to card variants
  landscape: 'horizontal-image',
  portrait: 'list',
  square: 'default',
  // Keep existing card variants
  'horizontal-image': 'horizontal-image',
  'side-image': 'side-image',
  default: 'default',
  list: 'list'
}

export const Event = ({ object, variant = 'side-image' }: EventProps) => {
  const {
    des: title = '',
    complement: description = '',
    obs: observations = '',
    document,
    holder_organization: holderOrganization,
    organizer_organization: organizerOrganizations
  } = object

  const creators = ((organizerOrganizations as YesObject[]) ?? []).reduce(
    (acc, creator, index, arr) => {
      return acc + (creator?.des || '') + (index < arr.length - 1 ? ' | ' : '')
    },
    ''
  )

  const documentFormat = object.document_format as DocumentFormat

  const imageVariant = documentFormatToVariantMap[documentFormat] || variant

  const holderName = (holderOrganization as YesObject[])?.[0]?.des || ''

  const establishment = (object.establishment?.[0] as YesObject)?.des
  const establishmentUuid = (object.establishment?.[0] as YesObject)?.uuid

  const startDate = object.start_date as string

  const endDate = object.end_date as string

  const city = (object.city?.[0] as YesObject)?.des
  const state = (object.state?.[0] as YesObject)?.sigla

  const documentImageUuid = (document as YesObject[])?.[0]?.uuid

  const { yesObjects: address, isFetching: isLoadingAddress } =
    useFetchYesObjects({
      filterBody: {
        query: {
          $and: [{ 'establishment_address.uuid': { $in: [establishmentUuid] } }]
        },
        objectclass: globalThis.addressObjectclassUuid
      },
      enabled: !!establishmentUuid
    })

  const { yesObject: imageDocumentObject } = useYesObject<DocumentObjectProps>({
    objectclassUUID: globalThis.idObjectclassDocument,
    objectUUID: documentImageUuid,
    showLoading: false,
    enabled: !!documentImageUuid
  })

  const location =
    !isLoadingAddress && !!address?.[0]
      ? `
    ${establishment ? `${establishment} | ` : ''}
    ${address?.[0]?.des},
    ${address?.[0]?.number ? `${address?.[0]?.number}, ` : ''}
    ${address?.[0]?.complement ? `${address?.[0]?.complement}, ` : ''}
    ${address?.[0]?.neighborhood}
    - ${city}/${state}`
      : ''

  const imageUrl = imageDocumentObject?.url?.[0]?.des || ''

  return (
    <EventCard
      title={title}
      description={description}
      startDate={startDate}
      endDate={endDate}
      creator={creators}
      organization={holderName}
      location={location}
      imageUrl={imageUrl}
      observations={observations}
      variant={imageVariant}
    />
  )
}

export default Event
